import { initializeApp } from 'firebase/app'
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  EmailAuthProvider,
  setPersistence,
  browserLocalPersistence,
} from 'firebase/auth'
import { getFirestore, setLogLevel } from 'firebase/firestore'
import { getStorage } from 'firebase/storage'
import { getMessaging, type Messaging } from 'firebase/messaging'
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  getToken,
} from 'firebase/app-check'
import { getAI, GoogleAIBackend } from 'firebase/ai'
import { logger } from '@/services/logger'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
}

const app = initializeApp(firebaseConfig)

// App Check (reCAPTCHA Enterprise). Required by Firebase AI Logic (Gemini)
// from 2026-11-02 — see docs/plan/implementation/app-check-ai-logic.md.
// Must run before any Firebase service makes requests.
if (typeof window !== 'undefined') {
  const recaptchaKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY

  if (
    recaptchaKey &&
    recaptchaKey !== 'disabled' &&
    recaptchaKey !== 'undefined'
  ) {
    // localhost can't pass reCAPTCHA (AC-D3): use the debug token registered
    // on the project, read from .env.local (gitignored). DEV builds only.
    const debugToken = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN
    if (import.meta.env.DEV && debugToken) {
      ;(
        self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string }
      ).FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken
    }

    try {
      const appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(recaptchaKey),
        isTokenAutoRefreshEnabled: true,
      })
      logger.debug('[Firebase] App Check initialized')
      // Surface a failed token mint loudly — once AI Logic is enforced, a
      // missing token means every Gemini call is rejected.
      getToken(appCheck)
        .then(() => logger.debug('[Firebase] App Check token minted'))
        .catch(error =>
          console.warn('[Firebase] App Check token mint failed:', error)
        )
    } catch (error) {
      console.warn('[Firebase] App Check initialization failed:', error)
    }
  } else {
    logger.debug('[Firebase] App Check disabled - no reCAPTCHA site key')
  }
}

export const auth = getAuth(app)

// Configure Firebase Auth persistence to use browser localStorage
// This ensures authentication persists across page refreshes and browser sessions
// CRITICAL FIX: Export async initialization function to prevent race condition
// where onAuthStateChanged fires before persistence is restored from localStorage
let authPersistenceInitialized = false
let authPersistencePromise: Promise<void> | null = null

export const initializeAuth = async (): Promise<void> => {
  // Return existing promise if initialization is already in progress
  if (authPersistencePromise) {
    return authPersistencePromise
  }

  // Return immediately if already initialized
  if (authPersistenceInitialized) {
    return Promise.resolve()
  }

  // PB-77 diagnostics: where the saved sign-in is (key names only, never
  // values): localStorage, and Firebase's IndexedDB store. Logged at load and
  // after setPersistence, which moves it between the two.
  const where = async (stage: string) => {
    try {
      const local = Object.keys(localStorage).filter(k =>
        k.startsWith('firebase:authUser')
      )
      const idb = await new Promise<string[]>(resolve => {
        const open = indexedDB.open('firebaseLocalStorageDb')
        open.onerror = () => resolve(['(open failed)'])
        open.onsuccess = () => {
          const dbh = open.result
          if (!dbh.objectStoreNames.contains('firebaseLocalStorage')) {
            dbh.close()
            return resolve([])
          }
          const req = dbh
            .transaction('firebaseLocalStorage', 'readonly')
            .objectStore('firebaseLocalStorage')
            .getAllKeys()
          req.onsuccess = () => {
            dbh.close()
            resolve(
              (req.result as unknown[])
                .map(String)
                .filter(k => k.startsWith('firebase:authUser'))
            )
          }
          req.onerror = () => resolve(['(read failed)'])
        }
      })
      console.info(`[AuthRestore] ${stage}`, {
        localStorage: local.length,
        indexedDB: idb.length,
        hasUser: !!auth.currentUser,
        swControlled: !!navigator.serviceWorker?.controller,
        ms: Math.round(performance.now()),
      })
    } catch (err) {
      console.info(`[AuthRestore] ${stage}: storage check failed`, err)
    }
  }
  void where('load')

  // Create and store the initialization promise
  authPersistencePromise = setPersistence(auth, browserLocalPersistence)
    .then(() => {
      authPersistenceInitialized = true
      void where('persistence set')
      logger.debug(
        '[Firebase] Auth persistence ready - localStorage persistence enabled'
      )
    })
    .catch(error => {
      console.error('[Firebase] Failed to set auth persistence:', error)
      // Still mark as initialized to prevent infinite retry
      authPersistenceInitialized = true
    })

  return authPersistencePromise
}

export const db = getFirestore(app)

// D-N-104 diagnostics: the Firestore Listen channel was seen dropping on the
// iPhone PWA (slow property load, "network connection lost"). Direct console
// calls so they show in Eruda on UAT, where logger output is off (GL-D8).
// Opt-in SDK debug log: in Eruda run
//   localStorage.setItem('mntnd-firestore-debug', '1')
// then reload; remove the key to turn it off. Remove this block once D-N-104
// is resolved.
if (typeof window !== 'undefined') {
  try {
    if (localStorage.getItem('mntnd-firestore-debug') === '1') {
      setLogLevel('debug')
      console.info('[Connection] Firestore SDK debug logging ON')
    }
  } catch {
    // localStorage unavailable — skip the opt-in
  }
  window.addEventListener('online', () => console.info('[Connection] online'))
  window.addEventListener('offline', () => console.warn('[Connection] offline'))
  document.addEventListener('visibilitychange', () =>
    console.info('[Connection] visibility:', document.visibilityState)
  )
}
export const storage = getStorage(app)

// D-N-021 diagnostic: storage uploads on UAT produce URLs with bucket
// `.appspot.com` instead of the configured `mntnd-app.firebasestorage.app`.
// Log resolved config + bucket at boot to confirm what the SDK saw.
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const storageInternal = storage as any
  logger.debug('[Trace boot] storage diagnostic', {
    envStorageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    appOptionsStorageBucket: app.options.storageBucket,
    sdkBucketString: storageInternal?._bucket?.bucket,
    sdkBucketPath: storageInternal?._bucket?.path_,
    sdkHost: storageInternal?._host,
    sdkUrl: storageInternal?._url,
  })
}

// Messaging is initialized lazily — only after the FCM service worker is registered.
// Eager initialization causes errors because the default SW (sw.js) doesn't handle FCM messages.
let _messaging: Messaging | null = null
export function getMessagingInstance(): Messaging | null {
  if (_messaging) return _messaging
  if (typeof window === 'undefined' || !('serviceWorker' in navigator))
    return null
  try {
    _messaging = getMessaging(app)
    return _messaging
  } catch (error) {
    console.warn('[Firebase] Messaging not supported on this browser:', error)
    return null
  }
}
// Keep backward-compatible export for code that reads `messaging` directly
export { _messaging as messaging }

// Firebase AI (Gemini) for equipment image analysis
export const ai = getAI(app, { backend: new GoogleAIBackend() })

// Authentication providers
export const googleProvider = new GoogleAuthProvider()
// Force Google's account picker on every sign-in. Without this, Google
// silently reuses the active session — which on PWA / shared-device test
// scenarios traps the user into a previously-authenticated account with no
// way to switch. One extra tap for single-account users; multi-account
// testing requires it.
googleProvider.setCustomParameters({ prompt: 'select_account' })
export const appleProvider = new OAuthProvider('apple.com')
// Note: Amazon OAuth provider removed - Firebase doesn't support Amazon OAuth
// Amazon authentication now handled via Login with Amazon JavaScript SDK
// See /docs/AMAZON_OAUTH_SECURITY.md for mobile security considerations
export const emailProvider = EmailAuthProvider
